import {
  useMemo,
  type FC,
  type PropsWithChildren,
  type ReactNode
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { Card, Text } from 'react-native-paper';

import {
  DATE_FORMAT,
  TIME_FORMAT
} from '@/features/post-editor/constants/dateTime';
import type { Poll } from '@/features/post-editor/types/poll';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { getFormattedDate } from '@/shared/utils/date';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import { dayjs } from '@/time';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    card: {
      backgroundColor: 'transparent',
      borderColor: theme.colors.outlineVariant
    },
    title: { padding: 16 },
    content: { gap: 16 },
    date: { paddingTop: 8, textAlign: 'center' }
  });

type Props = {
  poll?: Poll;
  footer?: ReactNode;
} & PropsWithChildren;

export const PollCard: FC<Props> = ({ poll, footer, children }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POST_EDITOR, {
    keyPrefix: 'components.poll.card'
  });

  const isExpired = dayjs().isAfter(poll?.expirationTime);

  return (
    poll && (
      <Card mode="outlined" style={styles.card}>
        <Card.Title
          title={poll.text}
          titleNumberOfLines={2}
          subtitle={
            <Text variant="labelMedium">
              {t(poll.multipleChoice ? 'multiselect' : 'singleSelect')}
            </Text>
          }
          style={styles.title}
        />
        <Card.Content style={styles.content}>
          {children}
          <Text variant="labelMedium" style={styles.date}>
            {t(isExpired ? 'endedAt' : 'endsAt', {
              date: getFormattedDate(poll.expirationTime, DATE_FORMAT),
              time: getFormattedDate(poll.expirationTime, TIME_FORMAT)
            })}
          </Text>
          {footer}
        </Card.Content>
      </Card>
    )
  );
};
